import { useEffect, useMemo, useRef, useState } from 'react';

import {
  buildWalletAnnouncementId,
  isCampaignAnnouncementDismissed,
  pickVisibleAnnouncement,
  persistCampaignAnnouncementDismissal,
} from '@/components/campaignAnnouncementUtils';
import type { CampaignAnnouncement } from '@/components/marketplaceHomeContent';
import type { ShoppingWalletStore } from '@/presentation/stores/ShoppingWalletStore';

interface AnnouncementContext {
  wallet: ShoppingWalletStore;
  userId: string | null;
  loadedUserId: string | null;
  ready: boolean;
  reward: CampaignAnnouncement | null;
  campaign: CampaignAnnouncement | null;
  spinAcquired: boolean;
  pathname?: string | null;
}

export function useWalletGiftAnnouncement(context: AnnouncementContext) {
  const { wallet, userId, loadedUserId, ready, reward, campaign, spinAcquired, pathname } = context;
  const previousPath = useRef(pathname);
  const [clock, setClock] = useState(() => Date.now());
  const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(new Set());
  const now = Math.max(clock, Date.now());
  const credits = ready && userId === loadedUserId && wallet.hasLoaded && !wallet.balanceError
    ? wallet.getUntouchedGiftCredits(now) : [];
  const creditIds = credits.map((credit) => credit.id).sort();
  const creditAnnouncementIds = creditIds.map((id) => buildWalletAnnouncementId({
    amount: '', currency: '', creditIds: [id],
  }, userId));
  const walletId = credits.length && userId
    ? buildWalletAnnouncementId({ amount: '', currency: '', creditIds }, userId) : null;
  const walletAnnouncement = useMemo<CampaignAnnouncement | null>(() => walletId ? {
    id: walletId,
    message: 'You have a gift in your Shopping Wallet',
    ctaLabel: 'Open',
    mobileCtaLabel: 'Open',
    priority: 'high',
    dismissible: true,
  } : null, [walletId]);
  const expiry = Math.min(...credits.flatMap((credit) => credit.expires_at
    ? [Date.parse(credit.expires_at)] : []));

  useEffect(() => {
    const changed = previousPath.current !== pathname;
    previousPath.current = pathname;
    if (changed && ready && userId && userId === loadedUserId) void wallet.refreshBalance();
  }, [pathname, ready, userId, loadedUserId, wallet]);

  useEffect(() => {
    if (!Number.isFinite(expiry)) return;
    const timer = window.setTimeout(() => setClock(Date.now()),
      Math.min(2_147_483_647, Math.max(1, expiry - Date.now())));
    return () => window.clearTimeout(timer);
  }, [expiry, clock]);

  useEffect(() => {
    if (!ready || !userId || userId !== loadedUserId) return;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      setClock(Date.now());
      void wallet.refreshBalance();
    };
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [ready, userId, loadedUserId, wallet]);

  const allCreditsDismissed = creditAnnouncementIds.length > 0 && creditAnnouncementIds.every(
    (id) => dismissedIds.has(id) || isCampaignAnnouncementDismissed(id));
  // Only explicit critical announcements interrupt a personal reward or untouched gift.
  // Spin customers still do not receive routine acquisition promotions.
  const urgentAnnouncement = campaign?.priority === 'critical' ? campaign : null;
  const routineAnnouncement = !urgentAnnouncement && !spinAcquired ? campaign : null;
  const candidates = [urgentAnnouncement, reward,
    allCreditsDismissed ? null : walletAnnouncement, routineAnnouncement];
  const dismissed = new Set(dismissedIds);
  for (const candidate of candidates) {
    if (candidate?.dismissible && isCampaignAnnouncementDismissed(candidate.id)) {
      dismissed.add(candidate.id);
    }
  }
  return {
    announcement: pickVisibleAnnouncement(candidates, dismissed),
    walletAnnouncement,
    dismiss: (id: string) => {
      const ids = id === walletId ? [id, ...creditAnnouncementIds] : [id];
      ids.forEach(persistCampaignAnnouncementDismissal);
      setDismissedIds((previous) => new Set([...previous, ...ids]));
    },
  };
}
